import React from 'react';
import { motion } from 'framer-motion';
import styles from './Services.module.css';

const services = [
  {
    title: 'Web Design',
    description: 'Beautiful, modern interfaces designed with your users in mind. We create layouts that are intuitive, accessible, and aligned with your brand.',
    highlights: ['Responsive design', 'Brand-aligned UI', 'User-focused layouts'],
  },
  {
    title: 'Web Development',
    description: 'Fast, reliable websites and web applications built with modern technology. From landing pages to full-featured platforms.',
    highlights: ['React & TypeScript', 'Performance optimized', 'SEO-ready'],
  },
  {
    title: 'Web Applications',
    description: 'Custom SaaS platforms, dashboards, and business tools that solve real problems. Real-time features, authentication, and scalable architecture.',
    highlights: ['SaaS platforms', 'Real-time features', 'Cloud deployment'],
  },
  {
    title: 'Mobile Apps',
    description: 'Cross-platform mobile applications that extend your product to iOS and Android with a single codebase.',
    highlights: ['React Native', 'iOS & Android', 'App store ready'],
  },
];

const process = [
  { step: '01', title: 'Discover', description: 'We learn about your goals, audience, and vision for the project.' },
  { step: '02', title: 'Design', description: 'We craft the look, feel, and user experience before writing a line of code.' },
  { step: '03', title: 'Build', description: 'We develop your product with clean code, regular updates, and attention to detail.' },
  { step: '04', title: 'Launch', description: 'We deploy, test, and make sure everything works perfectly on launch day.' },
];

const Services: React.FC = () => {
  return (
    <section className={styles.services}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">What We Do</h2>
          <p className="section-subtitle">
            End-to-end web design and development services to bring your ideas to life
          </p>
        </motion.div>

        <div className={styles.servicesGrid}>
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className={styles.serviceCard}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -6 }}
            >
              <h3 className={styles.serviceTitle}>{service.title}</h3>
              <p className={styles.serviceDescription}>{service.description}</p>
              <ul className={styles.serviceHighlights}>
                {service.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          className={styles.processSection}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className={styles.processTitle}>How We Work</h3>
          <div className={styles.processGrid}>
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                className={styles.processStep}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <span className={styles.stepNumber}>{item.step}</span>
                <h4 className={styles.stepTitle}>{item.title}</h4>
                <p className={styles.stepDescription}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
